import React, { useState } from 'react';
import { useThemeTokens } from '../../theme/ThemeProvider';

const Tabs = ({ 
  children, 
  defaultActiveTab = 0,
  activeTab: controlledActiveTab,
  onTabChange,
  variant = 'default',
  size = 'medium',
  className = '',
  style = {},
  ...props 
}) => {
  const { getComponent, getColor, getSpacing, getFontSize, getBorderRadius, animations } = useThemeTokens();
  const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveTab);
  
  const tabsComponent = getComponent('tabs');
  
  // Usar tab controlado o interno
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;
  
  const handleTabClick = (index) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(index);
    }
    if (onTabChange) {
      onTabChange(index);
    }
  };
  
  // Extraer tabs y content de children
  const tabs = [];
  const contents = [];
  
  React.Children.forEach(children, (child, index) => {
    if (child && child.type === TabPanel) {
      tabs.push({
        label: child.props.label,
        icon: child.props.icon,
        disabled: child.props.disabled,
        index: index
      });
      contents.push(child.props.children);
    }
  });
  
  const getTabStyles = (isActive, isDisabled) => {
    const baseStyles = {
      ...tabsComponent.tab,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      opacity: isDisabled ? 0.5 : 1,
      border: 'none',
      background: 'none',
      fontFamily: 'inherit',
      fontSize: size === 'small' ? getFontSize('sm') : size === 'large' ? getFontSize('lg') : getFontSize('base'),
      fontWeight: isActive ? '600' : '500',
      display: 'flex',
      alignItems: 'center',
      gap: getSpacing('xs'),
    };
    
    if (variant === 'pills') {
      return {
        ...baseStyles,
        backgroundColor: isActive ? getColor('primary.blue') : getColor('neutral.lightGray'),
        color: isActive ? getColor('neutral.white') : getColor('neutral.mediumGray'),
        borderRadius: getBorderRadius('full'),
        padding: `${getSpacing('sm')} ${getSpacing('lg')}`,
      };
    }
    
    if (variant === 'underline') {
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: isActive ? getColor('primary.blue') : getColor('neutral.mediumGray'),
        borderBottom: isActive ? `2px solid ${getColor('primary.blue')}` : '2px solid transparent',
        borderRadius: 0,
        padding: `${getSpacing('sm')} ${getSpacing('md')}`,
      };
    }
    
    // Default variant (centered pills like in design system)
    return {
      ...baseStyles,
      backgroundColor: isActive ? getColor('primary.blue') : getColor('neutral.lightGray'),
      color: isActive ? getColor('neutral.white') : getColor('neutral.mediumGray'),
    };
  };
  
  const containerStyles = {
    ...tabsComponent.container,
    justifyContent: variant === 'underline' ? 'flex-start' : 'center',
    borderBottom: variant === 'underline' ? `1px solid ${getColor('neutral.lightGray')}` : 'none',
    ...style,
  };
  
  const contentStyles = {
    marginTop: getSpacing('lg'),
    width: '100%',
  };
  
  return (
    <div className={`gaero-tabs ${className}`} {...props}>
      {/* Tab Headers */}
      <div style={containerStyles}>
        {tabs.map((tab, index) => (
          <button
            key={index}
            style={getTabStyles(activeTab === index, tab.disabled)}
            onClick={() => !tab.disabled && handleTabClick(index)}
            disabled={tab.disabled}
            className="gaero-tab"
          >
            {tab.icon && (
              <span className="tab-icon">{tab.icon}</span>
            )}
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div style={contentStyles}>
        {contents[activeTab]}
      </div>
    </div>
  );
};

// Componente TabPanel para usar como children
const TabPanel = ({ children, label, icon, disabled = false }) => {
  return <div className="gaero-tab-panel">{children}</div>;
};

Tabs.TabPanel = TabPanel;

export default Tabs;
export { TabPanel };
